import { apiClient } from '@/lib/api-client';

/**
 * G3.15 — Admin-side client for kitchen staff-to-station assignments.
 *
 * Mirrors the backend endpoints under `/kds/stations/...` and `/kds/assignments/...`.
 */

export interface StationWorkload {
  station_id: string;
  station_name: string;
  station_type: string;
  display_order: number;
  is_active: boolean;
  staff_count: number;
  pending_items: number;
  oldest_pending_age_seconds: number;
}

export interface StationAssignment {
  id: string;
  station_id: string;
  user_id: string;
  shift_id: string | null;
  location_id: string;
  assigned_by: string;
  assigned_at: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  station_name: string;
  station_type: string;
  user_name: string;
  user_email: string;
  user_role: string;
  shift_name: string | null;
}

export interface UnassignedStation {
  id: string;
  name: string;
  station_type: string;
  display_order: number;
}

export interface AssignStaffResponse {
  assignment: StationAssignment;
  warnings: string[];
}

function query(params: Record<string, string | undefined | null>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const kitchenAssignmentService = {
  async getWorkload(locationId: string): Promise<StationWorkload[]> {
    return apiClient<StationWorkload[]>(
      `/kds/stations/workload${query({ location_id: locationId })}`,
    );
  },

  async getUnassigned(locationId: string, shiftId?: string): Promise<UnassignedStation[]> {
    return apiClient<UnassignedStation[]>(
      `/kds/stations/unassigned${query({ location_id: locationId, shift_id: shiftId })}`,
    );
  },

  async listStationAssignments(
    stationId: string,
    locationId: string,
    shiftId?: string,
  ): Promise<StationAssignment[]> {
    return apiClient<StationAssignment[]>(
      `/kds/stations/${stationId}/assignments${query({ location_id: locationId, shift_id: shiftId })}`,
    );
  },

  async assign(
    stationId: string,
    data: { user_id: string; shift_id?: string | null },
  ): Promise<AssignStaffResponse> {
    return apiClient<AssignStaffResponse>(
      `/kds/stations/${stationId}/assignments`,
      { method: 'POST', body: { ...data } },
    );
  },

  async unassign(assignmentId: string): Promise<StationAssignment> {
    return apiClient<StationAssignment>(
      `/kds/assignments/${assignmentId}`,
      { method: 'DELETE' },
    );
  },
};
